import { useForm, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DataTable } from '@/components/backoffice/DataTable'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Trash2 } from 'lucide-react'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { StaffMember } from '@/types'

type Props = { staff: StaffMember[]; search: string; pagination: PaginationMeta }

const ACCESS_LEVELS = [
  { key: 'admin' as const, label: 'Admin' },
  { key: 'compliance' as const, label: 'Compliance' },
  { key: 'finance' as const, label: 'Finance' },
  { key: 'support' as const, label: 'Support' },
]

function AccessToggle({ staffId, accessKey, hasAccess }: { staffId: string; accessKey: string; hasAccess: boolean }) {
  const handleToggle = () => {
    if (hasAccess) {
      router.delete(`/backoffice/staff/${staffId}/${accessKey}_access`, { preserveScroll: true })
    } else {
      router.post(`/backoffice/staff/${staffId}/${accessKey}_access`, {}, { preserveScroll: true })
    }
  }

  return (
    <button onClick={handleToggle} title={hasAccess ? `Revoke ${accessKey}` : `Grant ${accessKey}`}>
      <Badge
        variant={hasAccess ? 'default' : 'outline'}
        className={`text-[11px] cursor-pointer transition-colors ${hasAccess ? '' : 'opacity-30 hover:opacity-60'}`}
      >
        {accessKey}
      </Badge>
    </button>
  )
}

function AddStaffForm() {
  const form = useForm({ staff: { email_address: '', access_level: 'support' } })

  return (
    <div className="border-b border-border/40 pb-5">
      <form onSubmit={e => { e.preventDefault(); form.post('/backoffice/staff', { preserveScroll: true, onSuccess: () => form.reset() }) }} className="flex items-end gap-3">
        <div className="flex-1 max-w-xs">
          <label htmlFor="email" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Email</label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={form.data.staff.email_address}
            onChange={e => form.setData('staff', { ...form.data.staff, email_address: e.target.value })}
            required
            className="h-8 text-sm"
          />
        </div>
        <div className="w-32">
          <label htmlFor="access_level" className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Level</label>
          <select
            id="access_level"
            value={form.data.staff.access_level}
            onChange={e => form.setData('staff', { ...form.data.staff, access_level: e.target.value })}
            className="flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {ACCESS_LEVELS.map(level => <option key={level.key} value={level.key}>{level.label}</option>)}
          </select>
        </div>
        <Button type="submit" size="sm" variant="outline" disabled={form.processing} className="h-8 text-[13px]">
          Grant Access
        </Button>
      </form>
      {form.errors && Object.keys(form.errors).length > 0 && (
        <p className="text-sm text-destructive mt-2">{Object.values(form.errors).flat().join(', ')}</p>
      )}
    </div>
  )
}

export default function StaffIndex({ staff, search, pagination }: Props) {
  const confirm = useConfirm()
  return (
    <BackofficeLayout title="Staff" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Staff' }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Staff Management</h1>
          <p className="text-sm text-muted-foreground">Manage backoffice access for platform staff</p>
        </div>

        <AddStaffForm />

        <SearchInput value={search} placeholder="Search by email..." />

        <div>
          <SectionHeader title="Staff Members" count={pagination.total} />
          <DataTable
            data={staff}
            keyFn={m => m.id}
            emptyMessage="No staff members yet. Add one above."
            columns={[
              { key: 'email', header: 'Email', className: 'flex-1', render: m => <span className="text-sm font-medium">{m.email_address}</span> },
              { key: 'access', header: 'Access', className: 'w-52', render: m => (
                <div className="flex items-center gap-1.5">
                  {ACCESS_LEVELS.map(level => (
                    <AccessToggle key={level.key} staffId={m.id} accessKey={level.key} hasAccess={m[level.key]} />
                  ))}
                </div>
              )},
              { key: 'actions', header: '', className: 'w-12', render: m => (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => { if (await confirm({ description: `Remove all access for ${m.email_address}?`, confirmLabel: 'Remove', destructive: true })) router.delete(`/backoffice/staff/${m.id}`) }}
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )},
            ]}
          />

          <Pagination pagination={pagination} />
        </div>
      </div>
    </BackofficeLayout>
  )
}
